
import { Component, Vue } from "vue-property-decorator";
import { Getter } from "vuex-class";
import {
  CashOrderSendStatusEnum,
  getOrderDetailStringField,
  ICashOrderDTO,
  IMonitorsFilterModel,
  IRequestedAmount,
  IRequestHistorialsService,
  ISolCustomerCommodityType,
  ISolCustomerCurrency,
  ISolCustomerPack,
  ISolCustomerSupply,
  IUserDetailData,
  normalizeOrderRequestedAmounts,
  normalizeOrderSupplies,
  resolveOrderDetailSource,
  SourceRequestViewEnum,
} from "@/entities";
import MonitorsFilter from "@/components/monitor/MonitorsFilter.vue";
import MonitorsTable from "@/components/monitor/MonitorsTable.vue";
import DetailOrderModal from "@/components/orders/DetailOrderModal.vue";
import { IApprovalsParams } from "@/services";
import IconMonitors from "@/components/common/icon/IconMonitors.vue";
import { SweetAlertResult } from "sweetalert2";

@Component({
  name: "MonitorsPage",
  components: { IconMonitors, DetailOrderModal, MonitorsTable, MonitorsFilter },
})
export default class MonitorsPage extends Vue {
  @Getter("getLoggedUser") getLoggedUser!: IUserDetailData;

  filters: IMonitorsFilterModel = this.defaultFilters();
  orders: ICashOrderDTO[] = [];
  monitorPacks: ISolCustomerPack[] = [];
  monitorCurrencies: ISolCustomerCurrency[] = [];
  monitorCommodityTypes: ISolCustomerCommodityType[] = [];
  monitorCommodityTypesServicePointId: string | null = null;
  selectedOrder: ICashOrderDTO | null = null;

  get countryCode(): string {
    return this.getLoggedUser?.country?.code || "";
  }

  get isSentVisible(): boolean {
    return this.filters.sendStatuses?.includes(CashOrderSendStatusEnum.SENT) ?? false;
  }

  async mounted(): Promise<void> {
    await this.fetchOrdersOnInit();
    await this.setupCurrencies();
  }

  defaultFilters(): IMonitorsFilterModel {
    return {
      countries: [],
      sendStatuses: [CashOrderSendStatusEnum.FAILED, CashOrderSendStatusEnum.PENDING_SENT],
      serviceCode: "",
      clientCodeOrDesc: "",
      subclientCodeOrDesc: "",
      servicepointCodeOrDesc: "",
      usernameOrId: "",
    };
  }

  get fromCreateAt(): string {
    const today = new Date();
    today.setDate(today.getDate() - 14);
    today.setHours(0, 0, 0, 0);
    return today.toISOString();
  }

  get toCreateAt(): string {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return today.toISOString();
  }

  async fetchOrdersOnInit(): Promise<void> {
    const params: IApprovalsParams = {
      sendStatuses: this.filters.sendStatuses,
      serviceCode: this.filters.serviceCode,
      orderCreatedAtFrom: this.fromCreateAt,
      orderCreatedAtTo: this.toCreateAt,
      sourceRequestView: SourceRequestViewEnum.MONITORS,
    };

    const res = await this.$services.approvals.getOrders(params);
    this.orders = res || [];
  }

  async fetchOrder(): Promise<void> {
    const params: IApprovalsParams = {
      sendStatuses: this.filters.sendStatuses,
      serviceCode: this.filters.serviceCode,
      countries: this.filters.countries,
      servicepointCodeOrDesc: this.filters.servicepointCodeOrDesc,
      subclientCodeOrDesc: this.filters.subclientCodeOrDesc,
      clientCodeOrDesc: this.filters.clientCodeOrDesc,
      orderCreatedAtFrom: this.fromCreateAt,
      orderCreatedAtTo: this.toCreateAt,
      usernameOrId: this.filters.usernameOrId,
      sourceRequestView: SourceRequestViewEnum.MONITORS,
    };
    const res = await this.$services.approvals.getOrders(params);
    this.orders = res || [];
  }

  async openDetailOrder(order: ICashOrderDTO): Promise<void> {
    await this.setupCommodityTypes(order);
    await this.fetchPacksForOrder(order);

    const detail = this.parseOrderDetail(order);
    if (!detail) {
      this.makeToast("warning", this.$t("message.approvals.detailReadError").toString());
      return;
    }

    this.selectedOrder = order;
    const detailOrderModal = this.$refs.detailOrderModal as DetailOrderModal;
    detailOrderModal.$emit("showDetailOrderModal", { ...detail, rawMessage: order.message });
  }

  async reprocessOrder(order: ICashOrderDTO): Promise<void> {
    const { isConfirmed } = await this.modalConfirm(
      this.$t("label.confirmation.to.reprocess.message.description").toString()
    );
    if (!isConfirmed) {
      return;
    }
    const orderId = order.id;
    const sendStatus = CashOrderSendStatusEnum.PENDING_SENT;
    await this.$services.order.updateOrderSendStatus(orderId, sendStatus);
    await this.fetchOrder();
  }

  async cancelOrder(order: ICashOrderDTO): Promise<void> {
    const { isConfirmed } = await this.modalConfirm(
      this.$t("label.confirmation.to.cancel.message.description").toString()
    );
    if (!isConfirmed) {
      return;
    }
    const orderId = order.id;
    const sendStatus = CashOrderSendStatusEnum.CANCELED;
    await this.$services.order.updateOrderSendStatus(orderId, sendStatus);
    await this.fetchOrder();
  }

  async onChangeVisibleSent(includeSent: boolean): Promise<void> {
    const currentStatuses = this.filters.sendStatuses || [];
    const hasSent = currentStatuses.includes(CashOrderSendStatusEnum.SENT);

    if (includeSent && !hasSent) {
      this.filters.sendStatuses = [...currentStatuses, CashOrderSendStatusEnum.SENT];
    } else if (!includeSent && hasSent) {
      this.filters.sendStatuses = currentStatuses.filter((s) => s !== CashOrderSendStatusEnum.SENT);
    } else {
      return;
    }

    await this.fetchOrder();
  }

  private async setupCurrencies(): Promise<void> {
    if (!this.countryCode) {
      return;
    }

    const response = await this.$services.solCustomer.currency?.getCurrency({
      countryCode: this.countryCode,
    });
    this.monitorCurrencies = response?.currencies || [];
  }

  private async setupCommodityTypes(order: ICashOrderDTO): Promise<void> {
    const servicePointId = order.servicePoint?.oidSol;
    if (!this.countryCode || !servicePointId) {
      this.monitorCommodityTypes = [];
      this.monitorCommodityTypesServicePointId = null;
      return;
    }

    if (this.monitorCommodityTypesServicePointId === servicePointId) {
      return;
    }

    const response = await this.$services.solCustomer.orders?.getSupply({
      countryCode: this.countryCode,
      servicePointId,
    });

    if (!response) {
      this.monitorCommodityTypes = [];
      this.monitorCommodityTypesServicePointId = null;
      return;
    }

    this.monitorCommodityTypes = response.commodityTypes || [];
    this.monitorCommodityTypesServicePointId = servicePointId;
  }

  private async fetchPacksForOrder(order: ICashOrderDTO): Promise<void> {
    const entityId = order.servicePoint?.subclient?.client?.oidSol;
    if (!this.countryCode || !entityId) {
      this.monitorPacks = [];
      return;
    }

    const response = await this.$services.solCustomer.orders?.getPacks({
      countryCode: this.countryCode,
      entityId,
    });
    this.monitorPacks = response?.packs || [];
  }

  private parseOrderDetail(order: ICashOrderDTO): IRequestHistorialsService | null {
    try {
      const parsed = JSON.parse(order.message);
      const detailSource = resolveOrderDetailSource(parsed);
      const productCode = detailSource?.productCode || order.productCode;
      const serviceType = this.normalizeServiceType(detailSource?.serviceType);
      const hasOrderTypeContext = Boolean(productCode) || typeof serviceType === "number";
      if (!hasOrderTypeContext) {
        return null;
      }

      const servicePoint = order.servicePoint;
      const subclient = servicePoint?.subclient;
      const client = subclient?.client;
      const createdAt =
        typeof order.createdAt === "string"
          ? order.createdAt
          : new Date(order.createdAt).toISOString();

      return {
        ...(detailSource || undefined),
        serviceType,
        productCode,
        serviceCode: detailSource?.serviceCode || order.serviceCode,
        requestedAmounts: this.normalizeRequestedAmounts(detailSource?.requestedAmounts),
        supplies: this.normalizeSupplies(
          (detailSource as Record<string, unknown> | null)?.supplies
        ),
        faultDescription: getOrderDetailStringField(detailSource, "faultDescription"),
        assistanceObservation:
          getOrderDetailStringField(detailSource, "assistanceObservation") ||
          getOrderDetailStringField(detailSource, "comments"),
        requestedTime: detailSource?.requestedTime || createdAt,
        createdAt: detailSource?.createdAt || createdAt,
        applicant:
          getOrderDetailStringField(detailSource, "applicant") ||
          getOrderDetailStringField(detailSource, "userName") ||
          order.user?.username,
        entityDescription:
          getOrderDetailStringField(detailSource, "entityDescription") ||
          this.formatCodeDescription(client?.codeSol, client?.description),
        centerDescription:
          getOrderDetailStringField(detailSource, "centerDescription") ||
          this.formatCodeDescription(subclient?.codeSol, subclient?.description),
        servicePointDescription:
          getOrderDetailStringField(detailSource, "servicePointDescription") ||
          this.formatCodeDescription(servicePoint?.codeSol, servicePoint?.description),
      } as IRequestHistorialsService;
    } catch {
      return null;
    }
  }

  private formatCodeDescription(code?: string, description?: string): string {
    if (code && description) {
      return `${code} - ${description}`;
    }
    return description || code || "";
  }

  private normalizeRequestedAmounts(value: unknown): IRequestedAmount[] | undefined {
    return normalizeOrderRequestedAmounts(value, {
      packs: this.monitorPacks,
      currencies: this.monitorCurrencies,
    });
  }

  private normalizeSupplies(value: unknown): ISolCustomerSupply[] | undefined {
    return normalizeOrderSupplies(value, this.monitorCommodityTypes);
  }

  private normalizeServiceType(value: unknown): number | undefined {
    if (typeof value === "number" && Number.isInteger(value)) {
      return value;
    }

    if (typeof value === "string" && value.trim() !== "") {
      const parsedValue = Number(value);
      return Number.isInteger(parsedValue) ? parsedValue : undefined;
    }

    return undefined;
  }

  private makeToast(variant: string, message: string): void {
    this.$bvToast.toast(message, {
      autoHideDelay: 4000,
      variant,
      toaster: "b-toaster-bottom-center",
      noCloseButton: true,
    });
  }

  async modalConfirm(text: string): Promise<SweetAlertResult> {
    return this.$swal({
      title: this.$t("message.approvals.confirmTitle").toString(),
      text: text,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: this.$t("label.yes").toString(),
      cancelButtonText: this.$t("label.no").toString(),
    });
  }
}
